import { useTranslate } from 'react-admin';
import { Box, Card, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import type { SubscriptionRecord } from 'src/pages/admin/services/vaultService';

import { LongId, NUMERIC, Stacked, VaultCell } from './cells';
import { RECORDS_NS as NS } from './constants';
import { subscriptionCells, subscriptionStatus } from './helpers';

/** PRD §14.6's fields, in its order: Order ID, vault, user, amount, units, fill NAV, status, time. */
const COLUMNS = ['orderId', 'vault', 'user', 'amount', 'units', 'fillNav', 'status', 'time'] as const;

export const SubscriptionsTable = ({ records }: { records: SubscriptionRecord[] }) => {
  const translate = useTranslate();

  if (records.length === 0) {
    return (
      <Card sx={{ p: 3 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {translate(`${NS}.empty`)}
        </Typography>
      </Card>
    );
  }

  return (
    <Card>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 1100 }}>
          <TableHead>
            <TableRow>
              {COLUMNS.map((c) => (
                <TableCell key={c} sx={{ whiteSpace: 'normal', verticalAlign: 'bottom' }}>
                  {translate(`${NS}.subscriptions.${c}`)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((r) => {
              const cells = subscriptionCells(r);
              const status = subscriptionStatus(r, translate);
              const timeNote =
                r.status === 'SUBSCRIBED'
                  ? translate(`${NS}.filledAt`, { at: cells.executedAt })
                  : translate(`${NS}.rejectedAt`, { at: cells.executedAt });
              return (
                <TableRow key={r.id} selected={r.isManagerStake}>
                  <TableCell sx={{ minWidth: 160 }}>
                    <LongId value={r.id} head={8} tail={6} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 160 }}>
                    <VaultCell name={r.vaultName} vaultId={r.vaultId} />
                  </TableCell>
                  <TableCell sx={{ minWidth: 220 }}>
                    <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                      <LongId value={r.address} />
                      {r.isManagerStake && (
                        <Chip size="small" color="primary" label={translate(`${NS}.managerStake`)} />
                      )}
                    </Stack>
                    <LongId value={r.holderId} head={8} tail={6} caption />
                  </TableCell>
                  <TableCell sx={NUMERIC}>{cells.amount}</TableCell>
                  <TableCell sx={NUMERIC}>{cells.units}</TableCell>
                  <TableCell sx={NUMERIC}>{cells.fillNav}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                      <Chip size="small" variant="outlined" color={status.color} label={status.label} />
                      {cells.refund?.refunded && (
                        <Chip size="small" variant="soft" color="default" label={translate(`${NS}.refunded`)} />
                      )}
                    </Stack>
                    {cells.refund && (
                      <Typography variant="caption" component="div" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        {translate(`${NS}.refundBasis`, { amount: cells.refund.basis })}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stacked main={cells.arrivedAt} note={timeNote} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Card>
  );
};
